import { useCallback } from 'react';
import type { SensorAlertBundle } from '@mark43/cad-api';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { getSensorLocationQuery } from './helpers';
import { useSensorsStore } from './use-sensors-store';

export type CreateEventFromAlert = (alert: SensorAlertBundle) => void;

/**
 * Starts a new CAD event from a sensor event: opens a new event panel with the camera's location
 * typed into its Location field, and the sensor event attached once the dispatcher saves it.
 *
 * **Nothing is resolved here, so there is nothing to wait on.** The camera's address (or its
 * coordinates) goes in as a search with the location picker open on the results. The dispatcher
 * picks the match, and the event panel classifies that pick exactly as it would one they had
 * typed — see `useSensorEventPrefill`. A camera with neither opens an ordinary new event. The
 * alert's mapped priority, when it has one, is selected by the same hook.
 *
 * **The link rides on the save.** The alert key goes into the new panel's `panelState`, which puts
 * it on the form's `sensorAlertKeys`; the event save applies that inside its own transaction, so
 * the event and its link commit together or not at all. Nothing is linked if the dispatcher closes
 * the panel without saving — which is the point of opening a panel rather than creating outright.
 *
 * `useOpenEventPanel` broadcasts to the events window, so this works from the map window, where
 * every surface that offers it lives.
 */
export function useCreateEventFromAlert(): CreateEventFromAlert {
    const sensorsStore = useSensorsStore();
    const openEventPanel = useOpenEventPanel();

    return useCallback(
        function createEventFromAlert(alert: SensorAlertBundle) {
            // The alert carries no location of its own; it comes from the camera.
            const locationQuery = getSensorLocationQuery(sensorsStore.getSensorForAlert(alert));

            openEventPanel({
                shouldFocusPanel: true,
                workspacePanelConfig: {
                    panelState: {
                        ...(locationQuery ? { initialLocationQuery: locationQuery } : {}),
                        // The priority mapped to this alert's type, when an admin has mapped one.
                        ...(alert.sensorPriority
                            ? {
                                  initialPriority: {
                                      label: alert.sensorPriority.label,
                                      value: alert.sensorPriority.value,
                                  },
                              }
                            : {}),
                        sensorAlertKeys: [alert.alertKey],
                    },
                },
            });
        },
        [sensorsStore, openEventPanel]
    );
}
